import ESigningFormApi from './ESigningForm.api';

export default class ESigningFormTemplates {
    /**
     * @description Fetch signing templates and populate dropdown
     * @param {*} form
     * @param {string} endpoint - Templates endpoint
     * @returns {Promise<void>}
     */
    static async fetchSigningTemplatesAsync(form, endpoint) {
        const loader = ESigningFormTemplates.showTemplatesLoader();
        try {
            const response = await ESigningFormApi.fetchJson(endpoint);

            if (response?.status === 200) {
                await ESigningFormTemplates.setSigningTemplateDropDown(form, response.body);
            }
        } finally {
            ESigningFormTemplates.hideTemplatesLoader(loader);
        }
    }

    /**
     * @description Ensure a pulsing bottom-linear-loader exists inside the templates dropdown wrapper and show it.
     * @returns {HTMLElement|null} The loader element, or null if the wrapper was not found.
     */
    static showTemplatesLoader() {
        const wrapper = document.getElementById('signingTemplates')?.closest('.init-bottom-linear-loader');
        if (!wrapper) {
            return null;
        }

        let loader = wrapper.querySelector(':scope > .bottom-linear-loader');
        if (!loader) {
            loader = document.createElement('div');
            loader.className = 'bottom-linear-loader';
            wrapper.appendChild(loader);
        } else {
            loader.classList.remove('hidden');
        }
        return loader;
    }

    /**
     * @description Hide the templates loader (if any).
     * @param {HTMLElement|null} loader
     * @returns void
     */
    static hideTemplatesLoader(loader) {
        if (loader) {
            loader.classList.add('hidden');
        }
    }

    /**
     * @description Populate signing template dropdown from API response
     * @param {*} templatesResponse - API response payload
     * @returns void
     */
    static async setSigningTemplateDropDown(form, templatesResponse) {
        const dropdown = document.getElementById('selectTemplate');
        if (!dropdown) {
            return;
        }

        const templates = ESigningFormTemplates.normalizeTemplates(templatesResponse)
            .filter((template) => template.id && template.name);
        const resetLabel = eSigningTranslation?.selectTemplate
            || document.querySelector('#signingTemplates .showcase a')?.textContent?.trim()
            || 'Välj avtal';

        dropdown.innerHTML = templates
            .map(
                (template) => `
                    <li
                    class="esigning-template"
                    id="${template.id}"
                    data-value="${template.id}"
                    data-duration="${template.duration}"
                    data-deadline-time="${template.deadlineTime || ''}"
                    data-form-type="${template.formType}"
                    data-include-seller-signer="${template.includeSellerSigner}"
                    data-settings-block-id="${template.templateSettingsBlockId || ''}">
                        <a href="javascript:void(0)">${template.name}</a>
                    </li>`
            )
            .join('');

        dropdown.innerHTML = `
            <li class="esigning-template esigning-template-reset selected" data-reset="true" data-value="">
                <a href="javascript:void(0)">${resetLabel}</a>
            </li>
        ${dropdown.innerHTML}`;

        if (templates.length === 1) {
            const onlyTemplate = dropdown.querySelector('li.esigning-template:not([data-reset="true"])');
            const templateLink = onlyTemplate?.querySelector('a');
            if (templateLink) {
                await ESigningFormTemplates.selectSigningTemplate(form, {
                    preventDefault: () => {},
                    target: templateLink
                });
            }
        }
    }

    /**
     * @description Normalize template response from ESigningTemplateResponseViewModel
     * @param {*} templatesResponse - API response payload
     * @returns {Array<{id: string, name: string, duration: number}>}
     */
    static normalizeTemplates(templatesResponse) {
        const templates = Array.isArray(templatesResponse?.signingTemplateItems)
            ? templatesResponse.signingTemplateItems
            : [];

        return templates.map((template) => ({
            id: template.id || '',
            name: template.friendlyName || '',
            duration: Number(template.duration) || 0,
            deadlineTime: template.deadlineTime || '',
            templateSettingsBlockId: template.templateSettingsBlockId || null,
            formType: template.formType || 'Default',
            includeSellerSigner: template.includeSellerSigner === true,
        }));
    }

    /**
     * @description Set the signing date picker to today + days and update hidden signingDuration input
     * @param {number} days
     * @returns void
     */
    static setSigningDateFromDays(days) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + days);

        const signingDuration = document.getElementById('signingDuration');
        if (signingDuration) {
            signingDuration.value = days;
        }

        const signingDateInput = document.getElementById('signingDate');
        if (signingDateInput) {
            const year = targetDate.getFullYear();
            const month = `${targetDate.getMonth() + 1}`.padStart(2, '0');
            const day = `${targetDate.getDate()}`.padStart(2, '0');
            signingDateInput.value = `${year}-${month}-${day}`;

            signingDateInput.dispatchEvent(
                new CustomEvent('esigning:setdate', { detail: { date: targetDate } })
            );
        }
    }

    /**
     * @description Handle signing template selection
     * @param {*} form
     * @param {Event} evt - Click event
     * @returns void
     */
    static async selectSigningTemplate(form, evt) {
        evt.preventDefault();
        const templateElement = evt.target.closest('li');
        if (templateElement) {
            const isResetOption = templateElement.dataset.reset === 'true';
            if (isResetOption) {
                ESigningFormTemplates.clearSigningTemplateSelection(form);
                return;
            }

            for (let item of document.querySelectorAll('#selectTemplate li')) {
                item.classList.remove('selected');
            }

            templateElement.classList.add('selected');

            const templateId = templateElement.id;
            const templateName = evt.target.textContent;
            const templateDuration = Number(templateElement.dataset.duration) || 0;
            const deadlineTime = templateElement.dataset.deadlineTime || '';
            const settingsBlockId = templateElement.dataset.settingsBlockId || '';
            const formType = templateElement.dataset.formType || 'Default';
            const includeSellerSigner = templateElement.dataset.includeSellerSigner === 'true';

            document.getElementById('signingTemplate').value = templateId;
            document.querySelector('#signingTemplates .showcase a').textContent = templateName;

            const templateDropdown = document.getElementById('selectTemplate');
            if (templateDropdown) {
                templateDropdown.style.display = 'none';
            }

            const pageId = document.getElementById('pageContentId')?.value || '';
            await ESigningFormTemplates.loadFormContentAsync(form, formType, pageId);
            ESigningFormTemplates.setSellerSignerVisibility(includeSellerSigner);

            if (typeof form.toggleStep1NextVisibility === 'function') {
                form.toggleStep1NextVisibility(true);
            }

            const settingsBlockIdInput = document.getElementById('templateSettingsBlockId');
            if (settingsBlockIdInput) {
                settingsBlockIdInput.value = settingsBlockId;
            }

            const deadlineTimeInput = document.getElementById('deadlineTime');
            if (deadlineTimeInput) {
                deadlineTimeInput.value = deadlineTime;
            }

            if (templateDuration > 0) {
                ESigningFormTemplates.setSigningDateFromDays(templateDuration);
            }
        }
    }

    static setSellerSignerVisibility(includeSellerSigner) {
        const step2View = document.getElementById('step2View');
        const sellerSection = document.querySelector('.e-signing-seller');
        if (!step2View || !sellerSection) return;

        step2View.dataset.includeSellerSigner = includeSellerSigner ? 'true' : 'false';
        sellerSection.hidden = !includeSellerSigner;
        sellerSection.style.display = includeSellerSigner ? '' : 'none';
        sellerSection.setAttribute('aria-hidden', includeSellerSigner ? 'false' : 'true');
    }

    static clearSigningTemplateSelection(form) {
        ESigningFormTemplates.setSellerSignerVisibility(false);

        for (let item of document.querySelectorAll('#selectTemplate li')) {
            item.classList.remove('selected');
        }

        const resetOption = document.querySelector('#selectTemplate li[data-reset="true"]');
        if (resetOption) {
            resetOption.classList.add('selected');
        }

        const signingTemplateInput = document.getElementById('signingTemplate');
        if (signingTemplateInput) {
            signingTemplateInput.value = '';
        }

        const showcaseLink = document.querySelector('#signingTemplates .showcase a');
        if (showcaseLink) {
            showcaseLink.textContent = form?.defaultTemplateLabel || showcaseLink.textContent;
        }

        const formContentContainer = document.getElementById('esigning-form-content-container');
        if (formContentContainer && form?.initialStep1ContentHtml) {
            formContentContainer.innerHTML = form.initialStep1ContentHtml;
        }

        const settingsBlockIdInput = document.getElementById('templateSettingsBlockId');
        if (settingsBlockIdInput) {
            settingsBlockIdInput.value = '';
        }

        const deadlineTimeInput = document.getElementById('deadlineTime');
        if (deadlineTimeInput) {
            deadlineTimeInput.value = '';
        }

        const signingDurationInput = document.getElementById('signingDuration');
        if (signingDurationInput) {
            signingDurationInput.value = '';
        }

        const dropdown = document.getElementById('selectTemplate');
        if (dropdown) {
            dropdown.style.display = 'none';
        }

        const nextButton = document.getElementById('Step1Next');
        if (nextButton) {
            nextButton.classList.add('disabled');
        }

        if (typeof form.toggleStep1NextVisibility === 'function') {
            form.toggleStep1NextVisibility(false);
        }
    }

    /**
     * @description Load form content partial (template-specific fields) and replace container content.
     * Dispatches 'esigning:formloaded' on document when done.
     * @param {*} form - ESigningFormBase instance
     * @param {string} formType - Form type key matching a view in /Views/ESigning/steps/forms/content/
     * @param {string|number} pageId
     * @returns {Promise<void>}
     */
    static async loadFormContentAsync(form, formType, pageId) {
        const container = document.getElementById('esigning-form-content-container');
        const loader = document.getElementById('agreement-loader');

        if (!container) {
            console.warn('Form content container not found');
            return;
        }

        if (loader) loader.style.display = 'block';

        try {
            const response = await fetch(
                `/api/e-signing/form-content?formType=${encodeURIComponent(formType)}&pageId=${encodeURIComponent(pageId)}`,
                { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
            );

            if (!response.ok) {
                console.error(`Failed to load form content for formType "${formType}": ${response.status}`);
                return;
            }

            const html = await response.text();
            container.innerHTML = html;

            document.dispatchEvent(
                new CustomEvent('esigning:formloaded', { detail: { formType, form } })
            );
        } catch (err) {
            console.error('Error loading form content:', err);
        } finally {
            if (loader) loader.style.display = 'none';
        }
    }
}
